"use client"

import Image from "next/image"
import type { components } from "umbraco"
import { getMediaUrl } from "umbraco"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"
import Container from "../../Container"
import { DesktopLayer, ImageWrapper, MobileLayer, Root } from "./styles"

interface Props {
  data: components["schemas"]["ImageBlockPropertiesModel"]
  settings?: Record<string, unknown> | null
  locale: string
  culture: string
  [key: string]: unknown
}

export default function ImageBlock({ data }: Props) {
  const imageItem = data.image?.items?.[0]
  const props = imageItem?.content?.properties as
    | Record<string, unknown>
    | undefined
  if (!props) return null

  const desktopRel = getMediaUrl(props.desktopImage)
  const imageUrl = desktopRel ? getUmbracoMediaUrl(desktopRel) : null
  // EGDE-597/EGDE-599 sweep: the block read only `desktopImage`, so an editor's
  // Mobile Image on the responsiveImage element was silently ignored. Falls back
  // to the desktop asset when no mobile crop is authored (matches the CMS field
  // description: "Uses the desktop image if left empty").
  const mobileRel = getMediaUrl(props.mobileImage)
  const mobileUrl = mobileRel ? getUmbracoMediaUrl(mobileRel) : imageUrl
  const alt = (props.altText as string) ?? ""

  if (!imageUrl) return null

  const isContained = data.layout === "contained"
  const hasBorderRadius = data.borderRadius === true

  const imageContent = (
    <ImageWrapper
      data-block="image-block"
      $borderRadius={hasBorderRadius}
      $contained={isContained}
    >
      <DesktopLayer>
        <Image
          src={imageUrl}
          alt={alt}
          fill
          sizes={
            isContained
              ? "(min-width: 1440px) 1200px, (min-width: 1280px) 1120px, 1px"
              : "(min-width: 1920px) 1560px, (min-width: 1440px) 1320px, (min-width: 1280px) 1200px, 1px"
          }
          style={{ objectFit: "cover" }}
        />
      </DesktopLayer>
      <MobileLayer>
        <Image
          src={mobileUrl ?? imageUrl}
          alt={alt}
          fill
          sizes={
            isContained
              ? "(max-width: 1279px) calc(100vw - 32px), 1px"
              : "(max-width: 1279px) 100vw, 1px"
          }
          style={{ objectFit: "cover" }}
        />
      </MobileLayer>
    </ImageWrapper>
  )

  return (
    <Root>
      {isContained ? (
        <Container paddingMobile>{imageContent}</Container>
      ) : (
        imageContent
      )}
    </Root>
  )
}
